import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiArrowClockwise, PiClipboardText, PiMagnifyingGlass, PiMicrosoftExcelLogo, PiPackage, PiTrophy, PiX } from 'react-icons/pi';
import {
    Badge,
    Button,
    Input,
    LoadingState,
    PageHeader,
    Text,
    Title,
} from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { hasRole } from '@v2/lib/auth';
import { useI18n } from '@v2/providers/I18nProvider';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import ReservationExpiryCountdown from '@v2/features/stock-reservations/ReservationExpiryCountdown';
import {
    StockPhaseBadge,
    StockShipmentLink,
    phaseFromSource,
} from '@v2/features/products/StockPhaseBadge';
import { cn } from '@v2/utils/cn';
import { DOT_STYLE, dotInlineStyle } from '@v2/features/colors/ColorDot';

type StockRequestRow = {
    id: number;
    status: string;
    source?: 'cd' | 'shipment';
    source_shipment_id?: number | null;
    source_shipment?: {
        id: number;
        label: string;
        stage: string;
        stage_label: string;
        client?: string | null;
    } | null;
    qty_requested: number;
    qty_approved: number | null;
    reason: string | null;
    decision_note: string | null;
    requested_at: string | null;
    reserved_at?: string | null;
    expires_at: string | null;
    hours_until_expiry: number | null;
    deal_won?: boolean;
    days_pending: number | null;
    available_to_request: number;
    requested_by: {
        id: number;
        name: string;
        email?: string | null;
        avatar_url?: string | null;
    } | null;
    deal: {
        id: number;
        title: string;
        internal_number: string | null;
        deal_number: string | null;
        owner_name: string | null;
        contact_name?: string | null;
        url_key?: string;
    } | null;
    color_swatches?: Array<{ slot: string; label: string; name: string | null; hex: string | null }>;
    order_product: {
        id: number;
        product_name: string;
        product_code: string | null;
        quantity: number;
    } | null;
    product: {
        id: number;
        code: string | null;
        name: string;
        image_url?: string | null;
    } | null;
};

const AVATAR_FALLBACK = '/img/favicon.png';

/** Mesmo limite de listForQueue no backend: acima disso a lista vem cortada. */
const LIST_LIMIT = 200;

/** Reserva perto de vencer ganha cor de alerta; o resto fica neutro. */
const EXPIRY_ALERT_HOURS = 12;

type StatusFilter = 'reserved' | 'cancelled' | 'expired' | 'fulfilled' | 'all';

function statusTone(status: string): 'warning' | 'success' | 'danger' | 'secondary' | 'info' {
    if (status === 'reserved' || status === 'pending' || status === 'approved') return 'warning';
    if (status === 'fulfilled') return 'success';
    if (status === 'cancelled' || status === 'rejected') return 'danger';
    if (status === 'expired') return 'secondary';
    return 'info';
}

function canCancelRow(row: StockRequestRow): boolean {
    if (row.status !== 'reserved') {
        return false;
    }
    const userId = window.__V2__?.user?.id;
    const isRequester = userId != null && row.requested_by?.id === userId;
    return isRequester || hasRole('commercial_director') || hasRole('admin');
}

export default function StockApprovalsPage() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [status, setStatus] = useState<StatusFilter>('reserved');
    const [search, setSearch] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const [cancelTarget, setCancelTarget] = useState<StockRequestRow | null>(null);
    const [cancelNote, setCancelNote] = useState('');

    // Busca reativa (sem Enter): aplica 300 ms depois de parar de digitar.
    useEffect(() => {
        const timer = window.setTimeout(() => setSearchApplied(search.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [search]);

    const statusLabel = (value: string) => {
        const key = `v2.stock_approvals.status_${value}`;
        const translated = t(key);
        return translated === key ? value : translated;
    };

    const query = useQuery({
        queryKey: ['stock-requests', status, searchApplied],
        queryFn: async () => {
            const { data } = await api.get<{ data: StockRequestRow[] }>('/stock-requests', {
                params: { status, search: searchApplied || undefined },
            });
            return data.data;
        },
        // Reserva expira/é baixada sozinha: a fila não pode ficar parada até recarregar.
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
    });

    const rows = query.data ?? [];
    const reservedCount = useMemo(
        () => rows.filter((r) => r.status === 'reserved' || r.status === 'pending' || r.status === 'approved').length,
        [rows],
    );

    const cancelMut = useMutation({
        mutationFn: async () => {
            if (!cancelTarget) return;
            const { data } = await api.post(`/stock-requests/${cancelTarget.id}/cancel`, { note: cancelNote.trim() || null });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data?.message ?? t('v2.stock_approvals.cancel_success'));
            setCancelTarget(null);
            setCancelNote('');
            void qc.invalidateQueries({ queryKey: ['stock-requests'] });
            void qc.invalidateQueries({ queryKey: ['meta', 'navigation'] });
            void qc.invalidateQueries({ queryKey: ['v2', 'notifications'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) => {
            toast.error(err?.response?.data?.message ?? t('v2.stock_approvals.cancel_error'));
        },
    });

    const filters = useMemo(
        () =>
            [
                { value: 'reserved' as const, label: t('v2.stock_approvals.filter_reserved') },
                { value: 'cancelled' as const, label: t('v2.stock_approvals.filter_cancelled') },
                { value: 'expired' as const, label: t('v2.stock_approvals.filter_expired') },
                { value: 'fulfilled' as const, label: t('v2.stock_approvals.filter_fulfilled') },
                { value: 'all' as const, label: t('v2.stock_approvals.filter_all') },
            ] as const,
        [t],
    );

    const canViewCdLocation = Boolean(window.__V2__?.can_view_cd_location);
    const [exporting, setExporting] = useState(false);

    // Varredura de propostas reservadas (Beto): mesmo filtro da tela, sem limite de linhas.
    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await api.get('/stock-requests/export', {
                params: { status, search: searchApplied || undefined },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(
                new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `reservas-estoque-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t('v2.stock_approvals.export_error'));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-5">
            <PageHeader
                title={t('v2.stock_approvals.title')}
                breadcrumb={[
                    { name: t('v2.nav.estoque'), href: '/estoque' },
                    { name: t('v2.stock_approvals.title') },
                ]}
            >
                <Button size="sm" variant="outline" onClick={handleExport} isLoading={exporting}>
                    <PiMicrosoftExcelLogo className="me-1.5 size-4 text-emerald-600" />
                    {t('v2.stock_approvals.export')}
                </Button>
            </PageHeader>

            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 sm:p-5">
                <div className="flex flex-col gap-1">
                    <Text className="text-sm text-gray-500 dark:text-zinc-400">{t('v2.stock_approvals.subtitle')}</Text>
                    {status === 'reserved' && query.isSuccess && (
                        <p className="text-xs text-gray-600 dark:text-zinc-300">
                            {reservedCount === 0
                                ? t('v2.stock_approvals.none_active')
                                : t('v2.stock_approvals.active_count', { count: String(reservedCount) })}
                        </p>
                    )}
                </div>

                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="-mx-1 overflow-x-auto px-1">
                        <div className="inline-flex rounded-xl bg-gray-100 p-1 dark:bg-zinc-800">
                            {filters.map((f) => (
                                <button
                                    key={f.value}
                                    type="button"
                                    onClick={() => setStatus(f.value)}
                                    className={cn(
                                        'whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition',
                                        status === f.value
                                            ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                                            : 'text-gray-500 hover:text-gray-800 dark:text-zinc-400 dark:hover:text-zinc-200',
                                    )}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="relative w-full md:max-w-sm">
                        <PiMagnifyingGlass className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                        <Input
                            className="ps-9 pe-9"
                            placeholder={t('v2.stock_approvals.search_placeholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                aria-label={t('v2.stock_approvals.clear_search')}
                                className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-zinc-800"
                            >
                                <PiX className="size-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {query.isPending && <LoadingState variant="table" />}

            {query.isError && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
                    {t('v2.stock_approvals.load_error')}
                    <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
                        <PiArrowClockwise className="me-1.5 size-4" />
                        {t('v2.stock_approvals.retry')}
                    </Button>
                </div>
            )}

            {rows.length >= LIST_LIMIT && (
                <p className="rounded-xl bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
                    {t('v2.stock_approvals.truncated', { count: LIST_LIMIT })}
                </p>
            )}

            {!query.isPending && !query.isError && rows.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-14 text-center dark:border-zinc-700 dark:bg-zinc-900">
                    <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
                        <PiPackage className="size-6 text-gray-400" />
                    </div>
                    <Title as="h3" className="text-base font-semibold text-gray-900 dark:text-zinc-100">
                        {searchApplied ? t('v2.stock_approvals.empty_search', { term: searchApplied }) : t('v2.stock_approvals.empty')}
                    </Title>
                    <Text className="mt-1 max-w-sm text-sm text-gray-500 dark:text-zinc-400">
                        {t('v2.stock_approvals.empty_hint')}
                    </Text>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                        {searchApplied && (
                            <Button size="sm" variant="outline" onClick={() => setSearch('')}>
                                {t('v2.stock_approvals.clear_search')}
                            </Button>
                        )}
                        <Link
                            to="/estoque/consulta"
                            className="inline-flex items-center rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
                        >
                            {t('v2.stock_approvals.go_consult')}
                        </Link>
                    </div>
                </div>
            )}

            {rows.length > 0 && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {rows.map((row) => {
                        const dealLabel =
                            row.deal?.internal_number ||
                            row.deal?.deal_number ||
                            (row.deal ? `#${row.deal.id}` : '—');
                        const isActive =
                            row.status === 'reserved' ||
                            row.status === 'pending' ||
                            row.status === 'approved';
                        const fromProcess = row.source === 'shipment';
                        const showCancel = canCancelRow(row);
                        const nearExpiry = isActive && row.hours_until_expiry !== null && row.hours_until_expiry <= EXPIRY_ALERT_HOURS;
                        const swatches = (row.color_swatches ?? []).filter((sw) => sw.name);
                        const lineQty = row.order_product?.quantity ?? row.qty_requested;

                        return (
                            <article
                                key={row.id}
                                className={cn(
                                    'flex h-full flex-col rounded-2xl border bg-white p-3.5 shadow-sm transition dark:bg-zinc-900',
                                    nearExpiry
                                        ? 'border-rose-200 dark:border-rose-500/30'
                                        : row.deal_won && isActive
                                          ? 'border-violet-200 dark:border-violet-500/30'
                                          : 'border-gray-200 dark:border-zinc-700',
                                )}
                            >
                                <div className="flex gap-3">
                                    <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800 sm:size-20">
                                        {row.product?.image_url ? (
                                            <img
                                                src={row.product.image_url}
                                                alt={row.order_product?.product_name ?? ''}
                                                className="size-full object-contain p-1.5"
                                            />
                                        ) : (
                                            <PiPackage className="size-7 text-gray-300 dark:text-zinc-600" />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {/* Status só quando a aba mistura status — em "Ativas" todo card é "Reservado". */}
                                            {status === 'all' && (
                                                <Badge color={statusTone(row.status)} variant="flat" className="text-[10px]">
                                                    {statusLabel(row.status)}
                                                </Badge>
                                            )}
                                            <StockPhaseBadge
                                                phase={phaseFromSource(row.source)}
                                                label={fromProcess
                                                    ? t('v2.stock_approvals.source_shipment')
                                                    : t('v2.stock_approvals.source_cd')}
                                            />
                                            {fromProcess && (
                                                <StockShipmentLink
                                                    shipmentId={row.source_shipment?.id ?? row.source_shipment_id}
                                                    label={row.source_shipment?.label}
                                                />
                                            )}
                                            {row.deal_won && (
                                                <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                                                    <PiTrophy className="size-3" />
                                                    {t('v2.stock_approvals.deal_won')}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="mt-1.5 line-clamp-2 text-sm font-semibold leading-snug text-gray-900 dark:text-zinc-100">
                                            {row.order_product?.product_name ??
                                                t('v2.stock_approvals.product_fallback')}
                                        </h3>
                                        {row.order_product?.product_code && (
                                            <p className="mt-0.5 font-mono text-[11px] text-gray-400 dark:text-zinc-400">
                                                {row.order_product.product_code}
                                            </p>
                                        )}
                                        {swatches.length > 0 && (
                                            <div className="mt-1 flex items-center gap-1.5" title={swatches.map((sw) => `${sw.label}: ${sw.name}`).join('\n')}>
                                                <span className="flex -space-x-1">
                                                    {swatches.map((sw) => (
                                                        <span
                                                            key={sw.slot}
                                                            role="img"
                                                            aria-label={`${sw.label}: ${sw.name}`}
                                                            className={cn('size-4 rounded-full border-2 border-white dark:border-zinc-900', !sw.hex && DOT_STYLE.off)}
                                                            style={dotInlineStyle(sw.hex ? 'standard' : 'off', sw.hex)}
                                                        />
                                                    ))}
                                                </span>
                                                <span className="truncate text-[11px] text-gray-500 dark:text-zinc-400">{swatches[0]?.name}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-gray-50 px-2.5 py-2 text-xs dark:bg-zinc-800/70">
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-400">
                                            {t('v2.stock_approvals.detail.sale')}
                                        </p>
                                        {row.deal ? (
                                            <Link
                                                to={ploomesDealPath(row.deal)}
                                                className="truncate font-semibold text-sky-600 hover:underline dark:text-sky-400"
                                            >
                                                #{dealLabel}
                                            </Link>
                                        ) : (
                                            <p className="font-medium text-gray-700 dark:text-zinc-300">—</p>
                                        )}
                                        <p className="truncate text-[11px] text-gray-500 dark:text-zinc-400" title={row.deal?.contact_name || row.deal?.title || ''}>
                                            {row.deal?.contact_name || row.deal?.title}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-400">
                                            {t('v2.stock_approvals.qty')}
                                        </p>
                                        <p className="text-base font-semibold tabular-nums text-gray-900 dark:text-zinc-100">
                                            {row.qty_requested}
                                            <span className="ms-0.5 text-[11px] font-normal text-gray-400 dark:text-zinc-400">
                                                {t('v2.stock_approvals.detail.of_line', { total: lineQty })}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {(row.expires_at || isActive) && (
                                    <div className="mt-2.5">
                                        <ReservationExpiryCountdown
                                            reservedAt={row.reserved_at ?? row.requested_at}
                                            expiresAt={row.expires_at}
                                            active={isActive}
                                            size="sm"
                                        />
                                    </div>
                                )}

                                {row.reason && (
                                    <p className="mt-2.5 line-clamp-2 rounded-lg bg-amber-50/80 px-2.5 py-1.5 text-[11px] leading-snug text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
                                        <span className="font-medium">
                                            {t('v2.stock_approvals.reason')}:{' '}
                                        </span>
                                        {row.reason}
                                    </p>
                                )}

                                <div className="mt-2.5 flex items-center gap-2">
                                    <img
                                        src={row.requested_by?.avatar_url || AVATAR_FALLBACK}
                                        alt=""
                                        className="size-6 shrink-0 rounded-full object-cover ring-1 ring-gray-200 dark:ring-zinc-600"
                                    />
                                    <p className="min-w-0 truncate text-[11px] text-gray-600 dark:text-zinc-300">
                                        {row.requested_by?.name ?? '—'}
                                    </p>
                                </div>

                                {row.deal_won && isActive && (
                                    <p className="mt-2.5 flex items-center gap-1.5 rounded-lg bg-violet-50 px-2.5 py-1.5 text-[11px] font-semibold text-violet-800 dark:bg-violet-500/10 dark:text-violet-200">
                                        <PiTrophy className="size-3.5 shrink-0" />
                                        {t('v2.stock_approvals.won_action')}
                                    </p>
                                )}

                                <div className="mt-auto flex gap-2 pt-3">
                                    <Link
                                        to={`/estoque/reservas/${row.id}`}
                                        className="inline-flex flex-1 items-center justify-center rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                    >
                                        {t('v2.stock_approvals.view_detail')}
                                    </Link>
                                    {canViewCdLocation && row.deal && row.source !== 'shipment' && row.status === 'reserved' && (
                                        <a
                                            href={`/admin/v1/ploomes-deals/${row.deal.id}/picking-list.pdf`}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={t('v2.stock_approvals.picking_list')}
                                            aria-label={t('v2.stock_approvals.picking_list')}
                                            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                        >
                                            <PiClipboardText className="size-3.5" />
                                        </a>
                                    )}
                                    {showCancel && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            title={t('v2.stock_approvals.cancel')}
                                            aria-label={t('v2.stock_approvals.cancel')}
                                            className="border-rose-200 px-2.5 text-xs text-rose-600 hover:bg-rose-50 dark:border-rose-500/30 dark:hover:bg-rose-500/10"
                                            onClick={() => setCancelTarget(row)}
                                        >
                                            <PiX className="me-1 size-3.5" />
                                            {t('v2.stock_approvals.cancel')}
                                        </Button>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            <V2Modal
                open={Boolean(cancelTarget)}
                onClose={() => { setCancelTarget(null); setCancelNote(''); }}
                title={t('v2.stock_approvals.cancel_title')}
                description={cancelTarget?.order_product?.product_code ?? undefined}
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setCancelTarget(null)}>
                            {t('v2.common.cancel')}
                        </Button>
                        <Button
                            className="bg-rose-600 text-white hover:bg-rose-700"
                            isLoading={cancelMut.isPending}
                            onClick={() => cancelMut.mutate()}
                        >
                            <PiX className="me-1.5 size-4" />
                            {t('v2.stock_approvals.cancel_confirm')}
                        </Button>
                    </>
                }
            >
                {cancelTarget && (
                    <div className="space-y-3">
                        <Text className="text-sm text-gray-600 dark:text-zinc-300">
                            {t('v2.stock_approvals.cancel_body', {
                                qty: String(cancelTarget.qty_requested),
                                product: cancelTarget.order_product?.product_name ?? '',
                            })}
                        </Text>
                        <label className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-zinc-200">{t('v2.stock_approvals.cancel_note')}</span>
                            <textarea
                                value={cancelNote}
                                onChange={(e) => setCancelNote(e.target.value)}
                                rows={2}
                                placeholder={t('v2.stock_approvals.cancel_note_ph')}
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
                            />
                        </label>
                    </div>
                )}
            </V2Modal>
        </div>
    );
}
